'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Check, Zap, Users, Building2, ChevronDown, AlertTriangle, Clock, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { TopupModal } from '@/components/briefing/topup-modal';
import { toast } from 'sonner';
import type { DbTier } from '@/lib/stripe-config';
import type { PricingPageData, TierConfig } from '@/types/pricing';

// ---------------------------------------------------------------------------
// Tier icon mapping
// ---------------------------------------------------------------------------

const TIER_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  starter: Zap,
  professional: Users,
  business: Building2,
};

// ---------------------------------------------------------------------------
// FAQ data (hardcoded — no need for DB-driven FAQs per spec)
// ---------------------------------------------------------------------------

const faqs = [
  {
    q: 'Kann ich jederzeit kündigen?',
    a: 'Ja. Sie können Ihr Abonnement jederzeit über das Kundenportal kündigen. Das Abo läuft bis zum Ende der bezahlten Periode weiter. Bitte beachten Sie: Bei Kündigung verfällt ungenutztes Briefing-Kontingent am Ende der laufenden Periode.',
  },
  {
    q: 'Wird ungenutztes Kontingent übertragen?',
    a: 'Ja. Nicht verbrauchte Briefings werden automatisch in den nächsten Monat übertragen, solange Ihr Abonnement aktiv ist. Der Übertrag ist auf maximal 3 Monatskontingente gedeckelt. So geht Ihnen nichts verloren, wenn Sie in einem Monat weniger Briefings erstellen.',
  },
  {
    q: 'Kann ich meinen Plan wechseln?',
    a: 'Ja. Sie können jederzeit über das Kundenportal in einen höheren oder niedrigeren Plan wechseln. Bei einem Upgrade wird die Differenz anteilig berechnet, bei einem Downgrade gilt der neue Plan ab der nächsten Abrechnungsperiode.',
  },
  {
    q: 'Wie lange dauert ein Briefing?',
    a: 'Ein Briefing wird in der Regel innerhalb von ca. 15 Minuten erstellt. Unsere KI-Agenten recherchieren dabei parallel aus mehreren Quellen und erstellen eine umfassende Analyse. Sie werden per E-Mail benachrichtigt, sobald Ihr Briefing fertig ist \u2014 Sie müssen also nicht warten.',
  },
  {
    q: 'Gibt es eine kostenlose Testphase?',
    a: 'Ja. Bei der Registrierung erhalten Sie kostenlose Start-Briefings, mit denen Sie die Plattform und die Qualität unserer Analysen unverbindlich testen können \u2014 ganz ohne Zahlungsdaten.',
  },
  {
    q: 'Was passiert, wenn mein Kontingent aufgebraucht ist?',
    a: 'Sobald Ihr monatliches Kontingent (inkl. übertragener Briefings) verbraucht ist, können Sie entweder in einen höheren Plan wechseln oder einzelne Briefings dazukaufen. Gekaufte Briefings verfallen nicht und stehen Ihnen jederzeit zur Verfügung.',
  },
  {
    q: 'Bekomme ich eine Rechnung?',
    a: 'Ja. Sie erhalten automatisch eine ordnungsgemäße Rechnung für jede Zahlung. Alle Rechnungen sind jederzeit im Kundenportal abrufbar und enthalten alle für die Buchhaltung relevanten Angaben.',
  },
  {
    q: 'Welche Zahlungsmethoden werden akzeptiert?',
    a: 'Wir akzeptieren Kreditkarten (Visa, Mastercard, Amex) und SEPA-Lastschrift. Die Zahlung wird sicher über Stripe abgewickelt.',
  },
  {
    q: 'Sind meine Daten sicher?',
    a: 'Ihre Briefings und Kontodaten werden ausschließlich auf Servern in Deutschland (Hetzner) gespeichert. Die Zahlungsabwicklung erfolgt über Stripe, einen zertifizierten Zahlungsdienstleister mit EU-konformer Datenverarbeitung. Wir sind vollständig DSGVO-konform.',
  },
];

// ---------------------------------------------------------------------------
// FAQ Item Component
// ---------------------------------------------------------------------------

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-4 min-h-[44px] text-left text-sm font-medium text-foreground hover:text-accent transition-colors"
      >
        {q}
        <ChevronDown
          className={`ml-4 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <p className="pb-4 text-sm text-muted-foreground leading-relaxed">{a}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campaign Banner Component
// ---------------------------------------------------------------------------

function CampaignBanner({ campaign }: { campaign: NonNullable<PricingPageData['activeCampaign']> }) {
  const isUrgent = campaign.effectiveStyle === 'urgent';
  const isWarning = campaign.effectiveStyle === 'warning';

  const bannerClass = isUrgent
    ? 'bg-red-50 border-b border-red-200 dark:bg-red-950/20 dark:border-red-900/40'
    : isWarning
      ? 'bg-amber-50 border-b border-amber-200 dark:bg-amber-950/20 dark:border-amber-900/40'
      : campaign.effectiveStyle === 'success'
        ? 'bg-green-50 border-b border-green-200 dark:bg-green-950/20 dark:border-green-900/40'
        : 'bg-accent/10 border-b border-accent/20';

  const textClass = isUrgent
    ? 'text-red-700 dark:text-red-400'
    : isWarning
      ? 'text-amber-700 dark:text-amber-400'
      : campaign.effectiveStyle === 'success'
        ? 'text-green-700 dark:text-green-400'
        : 'text-accent';

  return (
    <div className={bannerClass}>
      <div className="container mx-auto px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-2">
          {(isUrgent || isWarning) && (
            isUrgent ? (
              <Clock className={`h-4 w-4 shrink-0 ${textClass}`} />
            ) : (
              <AlertTriangle className={`h-4 w-4 shrink-0 ${textClass}`} />
            )
          )}
          <p className={`text-sm font-medium ${textClass}`}>
            <span className="font-bold">{campaign.title}</span>
            {': '}
            {campaign.description}
          </p>
        </div>
        {campaign.urgencyText && (
          <p className={`mt-1 text-xs font-semibold ${textClass}`}>
            {campaign.urgencyText}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pricing Card Component
// ---------------------------------------------------------------------------

function PricingCard({
  tier,
  billingPeriod,
  activeCampaign,
  isLoading,
  onSelect,
}: {
  tier: TierConfig;
  billingPeriod: 'monthly' | 'yearly';
  activeCampaign: PricingPageData['activeCampaign'];
  isLoading: boolean;
  onSelect: (tierId: DbTier, period: 'monthly' | 'yearly') => void;
}) {
  const Icon = TIER_ICONS[tier.id] ?? Zap;
  const isHighlighted = !!tier.highlightBadge;

  const displayPrice =
    billingPeriod === 'yearly' && tier.yearlyMonthlyEquivalent
      ? tier.yearlyMonthlyEquivalent
      : tier.monthlyPrice;

  // Campaign / coupon discount calculation
  const hasActiveCampaign = !!activeCampaign;
  const campaignAmountOff = hasActiveCampaign ? (activeCampaign as { amountOff?: number | null }).amountOff : null;
  const discountedPrice = hasActiveCampaign
    ? campaignAmountOff
      ? Math.max(0, Math.round(displayPrice - campaignAmountOff))
      : Math.round(displayPrice * (1 - activeCampaign!.discountPercent / 100))
    : null;

  // Yearly savings badge
  const yearlySavingPercent =
    billingPeriod === 'yearly' && tier.yearlyMonthlyEquivalent && tier.yearlyMonthlyEquivalent < tier.monthlyPrice
      ? Math.round((1 - tier.yearlyMonthlyEquivalent / tier.monthlyPrice) * 100)
      : null;

  // Determine CTA state
  const hasMonthlyPrice = !!tier.stripePriceIdMonthly;
  const hasYearlyPrice = !!tier.stripePriceIdYearly;
  const priceAvailable = billingPeriod === 'monthly' ? hasMonthlyPrice : hasYearlyPrice;
  const yearlyUnavailable = billingPeriod === 'yearly' && !hasYearlyPrice;

  return (
    <Card
      className={`relative flex flex-col card-shadow border-border ${
        isHighlighted ? 'border-accent ring-2 ring-accent/30 card-shadow-hover' : ''
      }`}
    >
      {tier.highlightBadge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="shadow-md bg-accent text-accent-foreground border-accent">
            {tier.highlightBadge}
          </Badge>
        </div>
      )}

      <CardHeader className="pb-4 pt-6 px-6">
        <div className="flex items-center gap-3 mb-3">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-lg ${
              isHighlighted
                ? 'bg-accent text-accent-foreground'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">{tier.displayName}</h2>
            <p className="text-xs text-muted-foreground">
              {tier.quotaPerMonth} Briefings / Monat
            </p>
          </div>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">{tier.description}</p>

        {/* Pricing */}
        <div className="mt-4">
          {hasActiveCampaign && discountedPrice !== null ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-extrabold text-foreground">
                  &euro;{discountedPrice.toLocaleString('de-DE')}
                </span>
                <span className="text-sm text-muted-foreground">/Monat</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground line-through">
                Listenpreis: &euro;{displayPrice.toLocaleString('de-DE')}/Monat
              </p>
              <Badge variant="secondary" className="mt-2 text-xs">
                {campaignAmountOff
                  ? `€${campaignAmountOff.toLocaleString('de-DE')} Rabatt`
                  : `${activeCampaign!.discountPercent}\u00a0% Rabatt`}
              </Badge>
              {tier.quotaPerMonth > 0 && discountedPrice !== null && discountedPrice > 0 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  ≈ €{(Math.round((discountedPrice / tier.quotaPerMonth) * 100) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pro Briefing
                </p>
              )}
            </>
          ) : (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-extrabold text-foreground">
                  &euro;{displayPrice.toLocaleString('de-DE')}
                </span>
                <span className="text-sm text-muted-foreground">/Monat</span>
              </div>
              {billingPeriod === 'yearly' && tier.yearlyMonthlyEquivalent && (
                <p className="mt-1 text-xs text-muted-foreground">
                  &euro;{(tier.yearlyMonthlyEquivalent * 12).toLocaleString('de-DE')}/Jahr
                </p>
              )}
              {yearlySavingPercent && (
                <Badge variant="secondary" className="mt-2 text-xs">
                  Spare {yearlySavingPercent}&nbsp;% vs. monatlich
                </Badge>
              )}
              {tier.quotaPerMonth > 0 && displayPrice > 0 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  ≈ €{(Math.round((displayPrice / tier.quotaPerMonth) * 100) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pro Briefing
                </p>
              )}
            </>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex flex-col flex-1 px-6 pb-6">
        {/* Features — briefing count always derived from quotaPerMonth, not from the stored string */}
        <ul className="flex-1 space-y-2 mb-6">
          {[
            `${tier.quotaPerMonth} Briefings pro Monat`,
            ...tier.features.filter((f) => !/^\d+\s+(KI-)?Briefings pro Monat/.test(f)),
          ].map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <span className="text-foreground">{f}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        {!tier.available ? (
          <Button variant="outline" className="w-full" disabled>
            {tier.comingSoonText ?? 'Demnächst verfügbar'}
          </Button>
        ) : yearlyUnavailable ? (
          <Button variant="outline" className="w-full" disabled>
            Jahrestarif nicht verfügbar
          </Button>
        ) : !priceAvailable ? (
          <Button variant="outline" className="w-full" disabled>
            Preiskonfiguration ausstehend
          </Button>
        ) : (
          <Button
            variant={isHighlighted ? 'accent' : 'outline'}
            className="w-full"
            disabled={isLoading}
            onClick={() => onSelect(tier.id as DbTier, billingPeriod)}
          >
            {isLoading ? 'Weiterleitung...' : 'Plan wählen'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Pricing Client Component
// ---------------------------------------------------------------------------

export default function PricingClient({
  pricingData,
  couponOverride,
  isLoggedIn = false,
  topupPriceCents = 7900,
}: {
  pricingData: PricingPageData;
  couponOverride?: { id: string; percentOff: number; amountOff: number | null } | null;
  /** PROJ-41: Whether the current user is logged in */
  isLoggedIn?: boolean;
  /** PROJ-41: Top-up price per briefing in cents (for no-plan users) */
  topupPriceCents?: number;
}) {
  const router = useRouter();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [topupOpen, setTopupOpen] = useState(false);

  const { tiers, activeCampaign: campaignFromServer, hasYearlyPricing } = pricingData;

  // If a coupon override is active, use its discount for price display.
  // Always override the banner text so it reflects the actual coupon, not the campaign.
  const couponDescription = couponOverride
    ? couponOverride.percentOff
      ? `${couponOverride.percentOff}% Rabatt`
      : `€${couponOverride.amountOff?.toLocaleString('de-DE')} Rabatt`
    : '';

  const activeCampaign = couponOverride
    ? {
        id: 'coupon-override',
        title: 'Sonderaktion',
        description: couponDescription,
        effectiveStyle: 'success' as const,
        fomoLevel: 'none' as const,
        urgencyText: null,
        stripeCouponId: couponOverride.id,
        expiresAt: null,
        discountPercent: couponOverride.percentOff,
        amountOff: couponOverride.amountOff,
      }
    : campaignFromServer;

  async function handleSelectPlan(tierId: DbTier, period: 'monthly' | 'yearly') {
    setLoadingTier(`${tierId}:${period}`);

    try {
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: tierId,
          billingPeriod: period,
          ...(couponOverride ? { couponId: couponOverride.id } : {}),
        }),
      });

      if (res.status === 401) {
        router.push('/login?from=/pricing');
        return;
      }

      if (res.status === 409) {
        toast.info(
          'Sie haben bereits ein aktives Abonnement. Weiterleitung zu Ihren Einstellungen…',
        );
        router.push('/dashboard/settings');
        return;
      }

      if (!res.ok) {
        const data = await res.json();
        if (data.code === 'no_price_configured') {
          toast.error('Für diesen Tarif ist noch kein Preis konfiguriert.');
        } else {
          toast.error(
            data.error || 'Checkout konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
          );
        }
        return;
      }

      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      console.error('[pricing] Network error:', err);
      toast.error('Netzwerkfehler. Bitte versuchen Sie es erneut.');
    } finally {
      setLoadingTier(null);
    }
  }

  return (
    <div className="bg-background">
      {/* Campaign Banner (dynamic) */}
      {activeCampaign && <CampaignBanner campaign={activeCampaign} />}

      {/* Header */}
      <section className="container mx-auto px-4 py-12 text-center md:py-16">
        <Badge variant="outline" className="mb-4">
          Preise
        </Badge>
        <h1 className="text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
          Einfache, transparente Preise
        </h1>
        <p className="mx-auto mt-4 max-w-lg text-muted-foreground">
          Monatlich kündbar &mdash; keine Mindestlaufzeit, keine versteckten Kosten.
          Alle Pläne beinhalten den vollen Funktionsumfang.
        </p>

        {/* Billing period toggle — only shown if yearly pricing is configured */}
        {hasYearlyPricing && (
          <div className="mt-6 inline-flex items-center rounded-lg border border-border bg-muted p-1 gap-1">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`rounded-md px-4 py-2 min-h-[44px] text-sm font-medium transition-colors ${
                billingPeriod === 'monthly'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monatlich
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`rounded-md px-4 py-2 min-h-[44px] text-sm font-medium transition-colors ${
                billingPeriod === 'yearly'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Jährlich
            </button>
          </div>
        )}
      </section>

      {/* Pricing Cards */}
      <section className="container mx-auto px-4 pb-16">
        <div className="grid gap-6 md:grid-cols-3 lg:gap-8 max-w-5xl mx-auto">
          {tiers.map((tier) => {
            const tierKey = `${tier.id}:${billingPeriod}`;
            return (
              <PricingCard
                key={tier.id}
                tier={tier}
                billingPeriod={billingPeriod}
                activeCampaign={activeCampaign}
                isLoading={loadingTier === tierKey}
                onSelect={handleSelectPlan}
              />
            );
          })}
        </div>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          Alle Preise zzgl. MwSt. &middot;{' '}
          {billingPeriod === 'monthly' ? 'Monatliche' : 'Jährliche'} Abrechnung &middot;{' '}
          <Link href={isLoggedIn ? '/dashboard' : '/register'} className="underline hover:text-foreground">
            {isLoggedIn ? 'Zum Dashboard' : 'Kostenlos registrieren'}
          </Link>
        </p>
        <p className="mt-3 text-center text-xs text-muted-foreground">
          Mit dem Kauf eines Abonnements akzeptieren Sie unsere{' '}
          <Link href="/agb" className="underline hover:text-foreground">
            AGB
          </Link>{' '}
          und{' '}
          <Link href="/datenschutz" className="underline hover:text-foreground">
            Datenschutzerklärung
          </Link>.
        </p>
      </section>

      {/* PROJ-41: Single briefing purchase section */}
      <section className="container mx-auto px-4 pb-16">
        <div className="mx-auto max-w-2xl rounded-xl border border-border bg-card p-8 text-center card-shadow">
          <h2 className="text-xl font-bold text-foreground mb-2">
            Ohne Abo testen
          </h2>
          <p className="text-muted-foreground mb-6">
            1 Briefing für &euro;{(topupPriceCents / 100).toLocaleString('de-DE', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })} &mdash; ohne Abonnement, kein Risiko.
            Kaufen Sie 1&ndash;5 Briefings, die nie verfallen.
          </p>
          {isLoggedIn ? (
            <Button
              variant="outline"
              size="lg"
              onClick={() => setTopupOpen(true)}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Jetzt kaufen
            </Button>
          ) : (
            <Button
              variant="outline"
              size="lg"
              onClick={() => router.push('/login?from=/pricing')}
            >
              Anmelden zum Kaufen
            </Button>
          )}
        </div>
      </section>

      {/* PROJ-41: Top-up modal */}
      <TopupModal
        open={topupOpen}
        onOpenChange={setTopupOpen}
        priceCents={topupPriceCents}
        cancelUrl="/pricing"
      />

      {/* FAQ */}
      <section className="bg-card border-t border-border py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <h2 className="mb-8 text-center text-2xl font-bold text-foreground">
            Häufige Fragen
          </h2>
          <div className="rounded-xl border border-border bg-background p-2 card-shadow">
            {faqs.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
